import { Component, Input, OnDestroy, OnInit, QueryList, ViewChildren } from '@angular/core';
import { ActivatedRoute, RouterLink, RouterLinkActive, RouterOutlet, Router } from '@angular/router';
import { ModalService } from '../../../../services/modal.service';
import { Observable, forkJoin } from 'rxjs';
import { map } from 'rxjs/operators';
import { I18nTranslator } from '../../../../i18n/translate';
import {
  ARBleAzoteFrequency,
  ARBleAzotePeriod,
  ARBleAzoteProductType,
  ARBleCIDestructionPeriod,
  ARBleCIDevelopment,
  ARBleCIType,
  ARBleEtatVegetatifSh,
  ARBlePastResiduType,
  ARBlePrairieAge,
  ARBlePrairieDestructionPeriod,
  ARBlePrairieExploitationMode,
  ARBlePrairiePostDestructionRank,
  ARBlePrairieType,
  ARVerseApportMoAutomne,
  ARVerseApportsMinerauxFractionnement,
} from '../../../../models/agrorendement-ble-lists.model';
import {
  ARConsoleFormatType,
  ARConsoleModulationType,
  ARConsoleType,
  ARConsoleVersion,
  ARFertilizerType,
} from '../../../../models/agrorendement-common-lists.model';
import {
  ARFertilizerData,
  ARFertilizerT1DataFacade,
  ARFertilizerT2DataFacade,
  ARFertilizerT3DataFacade,
  ARVraData,
  ARVraT1DataFacade,
  ARVraT2DataFacade,
  ARVraT3DataFacade,
  CbBatchEditT1,
  CbBatchEditT2,
  CbBatchEditT3,
} from '../../../../models/agrorendement-common.model';
import { AROrgeCampaign, AROrgeOrderItem, AROrgeParcel } from '../../../../models/agrorendement-orge.model';
import { Organisation } from '../../../../models/organisation.model';
import { CampaignYearPipe } from '../../../../pipes/campaignYear.pipe';
import { CapitalizePipe } from '../../../../pipes/capitalize.pipe';
import { ARConsoleService } from '../../../../services/agrorendement-common.service';
import { AROrgeService } from '../../../../services/agrorendement-orge.service';
import { BeApiService } from '../../../../services/beapi.service';
import { KeycloakService } from '../../../../services/keycloak.service';
import { OrganisationService } from '../../../../services/organisation.service';
import { ParcelService } from '../../../../services/parcel.service';
import { UserService } from '../../../../services/user.service';
import { Tooltip } from '../../../../utils/Tooltip';
import { TooltipContent } from '../../../../utils/TooltipContent';
import { getBatchOrderItems, prepareBatchSave } from '../../../../utils/batchedit.utils';
import { PreventDoubleClickDirective } from '../../../../utils/preventDoubleClick';
import { TOOLTIP_TEXTS } from '../../../../utils/tooltip-texts';
import { correctDate, showErrorMessage, toBoolean, toNumber } from '../../../../utils/utils';
import { ARAgrofertEditComponent } from '../common/agrofert-edit.component';
import { ARFertilizerEditComponent } from '../common/fertilizer-edit.component';
import { ARVerseSimpEditComponent } from '../common/verse-simp-edit.component';
import { ARVraEditComponent } from '../common/vra-edit.component';
import { AROrgeUtils } from './orge.utils';
import { TranslatePipe, TranslateWithPunctuationPipe } from '../../../../pipes/translate.pipe';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TooltipModule } from 'ngx-bootstrap/tooltip';

@Component({
  standalone: true,
  selector: 'ar-orge-orderItem-edit',
  templateUrl: 'orderItem-edit.component.html',
  imports: [RouterLink, RouterLinkActive, RouterOutlet, ARAgrofertEditComponent, Tooltip, TooltipContent, ARVraEditComponent, ARFertilizerEditComponent, PreventDoubleClickDirective, ARVerseSimpEditComponent, CampaignYearPipe, CapitalizePipe, TranslatePipe, TranslateWithPunctuationPipe, CommonModule, FormsModule, TooltipModule],
  providers: [AROrgeService, ARConsoleService, ParcelService, UserService, BeApiService, OrganisationService],
})
export class AROrgeOrderItemEditComponent implements OnInit, OnDestroy {
  @Input() orderItem: AROrgeOrderItem;

  @ViewChildren('fertilizerEditT1', { read: ARFertilizerEditComponent })
  fertilizerEditT1Ref: QueryList<ARFertilizerEditComponent>;
  @ViewChildren('fertilizerEditT2', { read: ARFertilizerEditComponent })
  fertilizerEditT2Ref: QueryList<ARFertilizerEditComponent>;
  @ViewChildren('fertilizerEditT3', { read: ARFertilizerEditComponent })
  fertilizerEditT3Ref: QueryList<ARFertilizerEditComponent>;
  @ViewChildren('vraEditT1', { read: ARVraEditComponent }) vraEditT1Ref: QueryList<ARVraEditComponent>;
  @ViewChildren('vraEditT2', { read: ARVraEditComponent }) vraEditT2Ref: QueryList<ARVraEditComponent>;
  @ViewChildren('vraEditT3', { read: ARVraEditComponent }) vraEditT3Ref: QueryList<ARVraEditComponent>;

  error: any;
  sub: any;

  edited = false;
  withinOrder = false;

  orderItemId: number;
  parcel: AROrgeParcel;

  currentCampaign: AROrgeCampaign;
  isT1OrderCurrentlyOpen = true;
  isT1DataCurrentlyOpen = true;
  isT2OrderCurrentlyOpen = true;
  isT2DataCurrentlyOpen = true;
  isT3OrderCurrentlyOpen = true;
  isT3DataCurrentlyOpen = true;

  t1FertilizerData: ARFertilizerData;
  t1VraData: ARVraData;
  t2FertilizerData: ARFertilizerData;
  t2VraData: ARVraData;
  t3FertilizerData: ARFertilizerData;
  t3VraData: ARVraData;

  tooltipTexts = TOOLTIP_TEXTS;

  private adviceTypeLockThresholdMs = 3 * 24 * 60 * 60 * 1000;
  isAdviceTypeLocked = false;

  commonConsoleModulationType: ARConsoleModulationType;

  isBatch = false;
  batchOrderItems: AROrgeOrderItem[];
  cbBatchEdit: Object = {};
  cbBatchEditT1: Object = {};
  cbBatchEditT2: Object = {};
  cbBatchEditT3: Object = {};
  hasT1Changed = false;
  hasT2Changed = false;
  hasT3Changed = false;

  isT1OrderPassedWarning = false;
  isT2OrderPassedWarning = false;
  isT3OrderPassedWarning = false;

  organisationsCanOrderPPF = [];
  canUserOrderPPF: boolean = false;
  canSpecyBeOrderedVerse: boolean = true;
  canSpecyBeOrderedPilotage: boolean = true;

  showBeApi = false;
  hasBeApi = false;

  isAdmin = false;

  t1t2Links = new Map<string, boolean>();
  t2t3Links = new Map<string, boolean>();

  constructor(
    private orgeService: AROrgeService,
    private parcelService: ParcelService,
    private beApiService: BeApiService,
    private i18nTranslator: I18nTranslator,
    private modal: ModalService,
    private router: Router,
    private route: ActivatedRoute,
    private userService: UserService,
  ) {
    this.isAdmin = KeycloakService.auth.authz.hasRealmRole('ROLE_ADMIN');
  }

  ngOnInit() {
    this.getOrganisation().subscribe((organisation) => {
      this.handleShowBeApi(organisation);
    });

    this.sub = this.route.params.subscribe((params) => {
      if (this.route.snapshot.url[4].path === 'edit') {
        if (params['parcelId']) {
          const parcelId = +params['parcelId'];
          const obs0 = this.orgeService.getParcel(parcelId);
          const obs1 = this.beApiService.getOrderItemByParcel(parcelId);

          forkJoin(obs0, obs1).subscribe((data: any) => {
            const baOI = data[1];
            if (baOI) {
              this.hasBeApi = baOI.hasBeApi;
            }

            const parcel = data[0];

            this.canSpecyBeOrderedVerse = this.canHandleVerse(parcel);
            this.canSpecyBeOrderedPilotage = this.canHandlePilotage(parcel);
            if (parcel.orderItem) {
              this.orderItemId = parcel.orderItem.id;
              this.edited = true;
              const obs1 = this.orgeService.getOrderItem(this.orderItemId);
              const obs2 = this.orgeService.getCampaigns();
              forkJoin(obs1, obs2).subscribe(
                (data: any) => {
                  this.orderItem = data[0];
                  const campaigns: AROrgeCampaign[] = data[1];
                  this.parcel = this.orderItem.parcel;
                  this.prepareOrderItem();
                  this.handleCampaigns(campaigns);
                  this.checkCampaignConfigurationDates();
                },
                (e) => this.handleGetError(e),
              );
            } else {
              this.parcel = parcel;
              this.edited = false;
              const obs2 = this.orgeService.getCampaigns();
              this.newOrderItem(parcelId);
              obs2.subscribe((campaigns: any) => {
                this.handleCampaigns(campaigns);
              });
            }
          });
        } else {
          showErrorMessage(this.modal, this.i18nTranslator.translate('ar.edit.error.pasDeParcelle')).then((r) => {
            this.router.navigate(['/orders', 'azote', 'orge', 'parcels']);
          });
        }
      } else if (this.route.snapshot.url[4].path === 'batch') {
        this.isBatch = true;
        this.edited = true;
        const parcelIds = params['parcelIds'].split('-').map((x: string) => +x);

        // FIXME faire plutôt un callback de getBatchOrderItems
        let i = 0;
        while (i < parcelIds.length && (this.canSpecyBeOrderedVerse || this.canSpecyBeOrderedPilotage)) {
          const obs0 = this.orgeService.getParcel(parcelIds[i]);
          obs0.subscribe((x: AROrgeParcel) => {
            if (!this.canHandleVerse(x)) {
              this.canSpecyBeOrderedVerse = false;
            }
            if (!this.canHandlePilotage(x)) {
              this.canSpecyBeOrderedPilotage = false;
            }
          });
          i++;
        }

        getBatchOrderItems(
          this.parcelService,
          this.orgeService,
          parcelIds,
          this,
          'AROrge',
          () => {
            return new AROrgeOrderItem();
          },
          () => {
            return new AROrgeParcel();
          },
          ['azote', 'orge'],
          () => {
            this.cbBatchEditT1 = new CbBatchEditT1(this.cbBatchEdit);
            this.cbBatchEditT2 = new CbBatchEditT2(this.cbBatchEdit);
            this.cbBatchEditT3 = new CbBatchEditT3(this.cbBatchEdit);
          },
        );

        this.orgeService.getCampaigns().subscribe((campaigns: any[]) => {
          this.handleCampaigns(campaigns);
          this.checkCampaignConfigurationDates();
        });
      }
    });
  }

  canHandleVerse(parcel: AROrgeParcel) {
    // que Orge d'hiver + orge de printemps + triticale
    return parcel.specy.id == 4 || parcel.specy.id == 5 || parcel.specy.id == 23;
  }

  canHandlePilotage(parcel: AROrgeParcel) {
    // que Orge d'hiver "autre" ou triticale
    return (parcel.specy.id == 4 && parcel.destination.id == 1) || parcel.specy.id == 23;
  }

  goBack(savedConfig: AROrgeOrderItem = null) {
    this.router.navigate(['/orders', 'azote', 'orge', 'parcels']);
    return false;
  }

  getOrganisation(): Observable<Organisation> {
    return this.userService.getMe().pipe(map((me) => me.organisation));
  }

  save() {
    // on nettoie sinon .id = null pas valide sur le serveur
    let restoreT1ConsoleModulationType = false;
    if (this.orderItem.t1ConsoleModulationType && !this.orderItem.t1ConsoleModulationType.id) {
      restoreT1ConsoleModulationType = true;
      this.orderItem.t1ConsoleModulationType = null;
    }
    let restoreT1ConsoleFormatType = false;
    if (this.orderItem.t1ConsoleFormatType && !this.orderItem.t1ConsoleFormatType.id) {
      restoreT1ConsoleFormatType = true;
      this.orderItem.t1ConsoleFormatType = null;
    }
    let restoreT1ConsoleType = false;
    if (this.orderItem.t1ConsoleType && !this.orderItem.t1ConsoleType.id) {
      restoreT1ConsoleType = true;
      this.orderItem.t1ConsoleType = null;
    }
    let restoreT1ConsoleVersion = false;
    if (this.orderItem.t1ConsoleVersion && !this.orderItem.t1ConsoleVersion.id) {
      restoreT1ConsoleVersion = true;
      this.orderItem.t1ConsoleVersion = null;
    }
    let restoreT1FertilizerType = false;
    if (this.orderItem.t1FertilizerType && !this.orderItem.t1FertilizerType.id) {
      restoreT1FertilizerType = true;
      this.orderItem.t1FertilizerType = null;
    }

    let restoreT2ConsoleModulationType = false;
    if (this.orderItem.t2ConsoleModulationType && !this.orderItem.t2ConsoleModulationType.id) {
      restoreT2ConsoleModulationType = true;
      this.orderItem.t2ConsoleModulationType = null;
    }
    let restoreT2ConsoleFormatType = false;
    if (this.orderItem.t2ConsoleFormatType && !this.orderItem.t2ConsoleFormatType.id) {
      restoreT2ConsoleFormatType = true;
      this.orderItem.t2ConsoleFormatType = null;
    }
    let restoreT2ConsoleType = false;
    if (this.orderItem.t2ConsoleType && !this.orderItem.t2ConsoleType.id) {
      restoreT2ConsoleType = true;
      this.orderItem.t2ConsoleType = null;
    }
    let restoreT2ConsoleVersion = false;
    if (this.orderItem.t2ConsoleVersion && !this.orderItem.t2ConsoleVersion.id) {
      restoreT2ConsoleVersion = true;
      this.orderItem.t2ConsoleVersion = null;
    }
    let restoreT2FertilizerType = false;
    if (this.orderItem.t2FertilizerType && !this.orderItem.t2FertilizerType.id) {
      restoreT2FertilizerType = true;
      this.orderItem.t2FertilizerType = null;
    }

    let restoreT3ConsoleModulationType = false;
    if (this.orderItem.t3ConsoleModulationType && !this.orderItem.t3ConsoleModulationType.id) {
      restoreT3ConsoleModulationType = true;
      this.orderItem.t3ConsoleModulationType = null;
    }
    let restoreT3ConsoleFormatType = false;
    if (this.orderItem.t3ConsoleFormatType && !this.orderItem.t3ConsoleFormatType.id) {
      restoreT3ConsoleFormatType = true;
      this.orderItem.t3ConsoleFormatType = null;
    }
    let restoreT3ConsoleType = false;
    if (this.orderItem.t3ConsoleType && !this.orderItem.t3ConsoleType.id) {
      restoreT3ConsoleType = true;
      this.orderItem.t3ConsoleType = null;
    }
    let restoreT3ConsoleVersion = false;
    if (this.orderItem.t3ConsoleVersion && !this.orderItem.t3ConsoleVersion.id) {
      restoreT3ConsoleVersion = true;
      this.orderItem.t3ConsoleVersion = null;
    }
    let restoreT3FertilizerType = false;
    if (this.orderItem.t3FertilizerType && !this.orderItem.t3FertilizerType.id) {
      restoreT3FertilizerType = true;
      this.orderItem.t3FertilizerType = null;
    }

    let restorePastAzoteFrequency = false;
    if (this.orderItem.pastAzoteFrequency && !this.orderItem.pastAzoteFrequency.id) {
      restorePastAzoteFrequency = true;
      this.orderItem.pastAzoteFrequency = null;
    }

    let restoreVerseApportMoAutomne = false;
    if (this.orderItem.verseApportMoAutomne && !this.orderItem.verseApportMoAutomne.id) {
      restoreVerseApportMoAutomne = true;
      this.orderItem.verseApportMoAutomne = null;
    }
    let restoreVerseApportsMinerauxFractionnement = false;
    if (this.orderItem.verseApportsMinerauxFractionnement && !this.orderItem.verseApportsMinerauxFractionnement.id) {
      restoreVerseApportsMinerauxFractionnement = true;
      this.orderItem.verseApportsMinerauxFractionnement = null;
    }

    // FIXME suppr PPF
    let restoreEtatVegetatifSh = false;
    if (!this.orderItem.etatVegetatifSh.id) {
      restoreEtatVegetatifSh = true;
      this.orderItem.etatVegetatifSh = null;
    }
    let restoreFirstAzoteProductType = false;
    if (!this.orderItem.firstAzoteProductType.id) {
      restoreFirstAzoteProductType = true;
      this.orderItem.firstAzoteProductType = null;
    }
    let restoreFirstAzotePeriod = false;
    if (!this.orderItem.firstAzotePeriod.id) {
      restoreFirstAzotePeriod = true;
      this.orderItem.firstAzotePeriod = null;
    }
    let restoreSecondAzoteProductType = false;
    if (!this.orderItem.secondAzoteProductType.id) {
      restoreSecondAzoteProductType = true;
      this.orderItem.secondAzoteProductType = null;
    }
    let restoreSecondAzotePeriod = false;
    if (!this.orderItem.secondAzotePeriod.id) {
      restoreSecondAzotePeriod = true;
      this.orderItem.secondAzotePeriod = null;
    }
    let restorePastAzoteProductType = false;
    if (!this.orderItem.pastAzoteProductType.id) {
      restorePastAzoteProductType = true;
      this.orderItem.pastAzoteProductType = null;
    }
    let restorePastAzotePreviousCultureProductType = false;
    if (!this.orderItem.pastAzotePreviousCultureProductType.id) {
      restorePastAzotePreviousCultureProductType = true;
      this.orderItem.pastAzotePreviousCultureProductType = null;
    }
    let restorePastResiduType = false;
    if (!this.orderItem.pastResiduType.id) {
      restorePastResiduType = true;
      this.orderItem.pastResiduType = null;
    }
    let restorePrairieAge = false;
    if (!this.orderItem.prairieAge.id) {
      restorePrairieAge = true;
      this.orderItem.prairieAge = null;
    }
    let restorePrairieDestructionPeriod = false;
    if (!this.orderItem.prairieDestructionPeriod.id) {
      restorePrairieDestructionPeriod = true;
      this.orderItem.prairieDestructionPeriod = null;
    }
    let restorePrairiePostDestructionRank = false;
    if (!this.orderItem.prairiePostDestructionRank.id) {
      restorePrairiePostDestructionRank = true;
      this.orderItem.prairiePostDestructionRank = null;
    }
    let restorePrairieExploitationMode = false;
    if (!this.orderItem.prairieExploitationMode.id) {
      restorePrairieExploitationMode = true;
      this.orderItem.prairieExploitationMode = null;
    }
    let restorePrairieType = false;
    if (!this.orderItem.prairieType.id) {
      restorePrairieType = true;
      this.orderItem.prairieType = null;
    }
    let restoreCIType = false;
    if (!this.orderItem.ciType.id) {
      restoreCIType = true;
      this.orderItem.ciType = null;
    }
    let restoreCIDevelopment = false;
    if (!this.orderItem.ciDevelopment.id) {
      restoreCIDevelopment = true;
      this.orderItem.ciDevelopment = null;
    }
    let restoreCIDestructionPeriod = false;
    if (!this.orderItem.ciDestructionPeriod.id) {
      restoreCIDestructionPeriod = true;
      this.orderItem.ciDestructionPeriod = null;
    }

    try {
      this.orderItem.objective = toNumber(this.orderItem.objective);
      this.orderItem.reliquatSh = toNumber(this.orderItem.reliquatSh);
      this.orderItem.firstAzoteConcentration = toNumber(this.orderItem.firstAzoteConcentration);
      this.orderItem.firstAzoteDensity = toNumber(this.orderItem.firstAzoteDensity);
      this.orderItem.secondAzoteConcentration = toNumber(this.orderItem.secondAzoteConcentration);
      this.orderItem.secondAzoteDensity = toNumber(this.orderItem.secondAzoteDensity);
      this.orderItem.pastAzotePreviousCultureConcentration = toNumber(
        this.orderItem.pastAzotePreviousCultureConcentration,
      );
      this.orderItem.pastAzotePreviousCultureDensity = toNumber(this.orderItem.pastAzotePreviousCultureDensity);
      this.orderItem.t1FertilizerConcentration = toNumber(this.orderItem.t1FertilizerConcentration);
      this.orderItem.t2FertilizerConcentration = toNumber(this.orderItem.t2FertilizerConcentration);
      this.orderItem.t3FertilizerConcentration = toNumber(this.orderItem.t3FertilizerConcentration);
    } catch (e) {
      // TODO détails plus précis ?
      showErrorMessage(this.modal, this.i18nTranslator.translate('ar.edit.error.invalidNumber'));
    }

    // traitement du cas pas zonage: valeurs par défaut pour les données
    // fertiliseur
    if (!this.orderItem.hasZonage) {
      // FIXME suppr dummy values
      this.orderItem.t2FertilizerType = new ARFertilizerType();
      // Solide
      this.orderItem.t2FertilizerType.id = 1;
      this.orderItem.t2FertilizerConcentration = 33.5;
      this.orderItem.t2ConsoleModulationType = new ARConsoleModulationType();
      // Aucune
      this.orderItem.t2ConsoleModulationType.id = 1;

      this.orderItem.t3FertilizerType = new ARFertilizerType();
      // Solide
      this.orderItem.t3FertilizerType.id = 1;
      this.orderItem.t3FertilizerConcentration = 33.5;
      this.orderItem.t3ConsoleModulationType = new ARConsoleModulationType();
      // Aucune
      this.orderItem.t3ConsoleModulationType.id = 1;

      if (this.isBatch) {
        // pour que les champs soient pris en compte pour le batch
        (<any>this.cbBatchEdit)['t2FertilizerType'] = true;
        (<any>this.cbBatchEdit)['t2FertilizerConcentration'] = true;
        (<any>this.cbBatchEdit)['t2ConsoleModulationType'] = true;

        (<any>this.cbBatchEdit)['t3FertilizerType'] = true;
        (<any>this.cbBatchEdit)['t3FertilizerConcentration'] = true;
        (<any>this.cbBatchEdit)['t3ConsoleModulationType'] = true;
      }
    }

    if (this.isBatch) {
      const obss: Observable<AROrgeOrderItem>[] = [];
      prepareBatchSave(this.orderItem, this.batchOrderItems, this.cbBatchEdit).forEach((batchOrderItem: any) => {
        if (this.hasT1Changed || !batchOrderItem.id) {
          batchOrderItem.hasT1 = (<any>this.cbBatchEdit)['hasT1'];
          if (!batchOrderItem.hasT1) {
            this.prepareNewT1Data(batchOrderItem);
          }
        }
        if (this.hasT2Changed || !batchOrderItem.id) {
          // setter le T2 et T3 (doivent obligatoirement remplis et pas null)
          // mais checkbox edition lot confondue avec valeur
          // quand null => false
          // TODO changer le nom car cbBatchEdit ne correspond pas vraiment
          // TODO la valeur est prise pour hasT2 (donc même si False, considéré
          // TODO comme la valeur pour hasT2 de tous les items du batch)
          // TODO alors que pour les autres champs, si False, pas de maj de la valeur
          batchOrderItem.hasT2 = (<any>this.cbBatchEdit)['hasT2'];
          // les champs ont été masqués au lieu de vidés, on les vide
          if (!batchOrderItem.hasT2) {
            this.prepareNewT2Data(batchOrderItem);
          }
        }
        if (this.hasT3Changed || !batchOrderItem.id) {
          batchOrderItem.hasT3 = (<any>this.cbBatchEdit)['hasT3'];

          if (!batchOrderItem.hasT3) {
            this.prepareNewT3Data(batchOrderItem);
          }
        }

        if ((<any>this.cbBatchEdit)['consoleModulationType']) {
          // identique : si pas hasT2 ou ... rempli quand même mais pas d'importance
          batchOrderItem.t1ConsoleModulationType = this.commonConsoleModulationType;
          batchOrderItem.t2ConsoleModulationType = this.commonConsoleModulationType;
          batchOrderItem.t3ConsoleModulationType = this.commonConsoleModulationType;
        }

        obss.push(this.orgeService.saveOrderItem(batchOrderItem));
      });

      forkJoin(obss).subscribe(
        (orderItems) => {
          this.goBack();
        },
        (error) => this.handleSavebatchError(error),
      );
    } else {
      this.orgeService.saveOrderItem(this.orderItem).subscribe(
        (orderItem) => {
          if (!this.withinOrder) {
            this.goBack(orderItem);
          } else {
            this.confirmOrder(orderItem.parcel.id);
          }
        },
        (error) => this.handleSaveError(error),
      );
    }

    if (restoreT1ConsoleModulationType) {
      this.orderItem.t1ConsoleModulationType = new ARConsoleModulationType();
      this.orderItem.t1ConsoleModulationType.id = null;
    }
    if (restoreT1ConsoleFormatType) {
      this.orderItem.t1ConsoleFormatType = new ARConsoleFormatType();
      this.orderItem.t1ConsoleFormatType.id = null;
    }
    if (restoreT1ConsoleType) {
      this.orderItem.t1ConsoleType = new ARConsoleType();
      this.orderItem.t1ConsoleType.id = null;
    }
    if (restoreT1ConsoleVersion) {
      this.orderItem.t1ConsoleVersion = new ARConsoleVersion();
      this.orderItem.t1ConsoleVersion.id = null;
    }
    if (restoreT1FertilizerType) {
      this.orderItem.t1FertilizerType = new ARFertilizerType();
      this.orderItem.t1FertilizerType.id = null;
    }

    if (restoreT2ConsoleModulationType) {
      this.orderItem.t2ConsoleModulationType = new ARConsoleModulationType();
      this.orderItem.t2ConsoleModulationType.id = null;
    }
    if (restoreT2ConsoleFormatType) {
      this.orderItem.t2ConsoleFormatType = new ARConsoleFormatType();
      this.orderItem.t2ConsoleFormatType.id = null;
    }
    if (restoreT2ConsoleType) {
      this.orderItem.t2ConsoleType = new ARConsoleType();
      this.orderItem.t2ConsoleType.id = null;
    }
    if (restoreT2ConsoleVersion) {
      this.orderItem.t2ConsoleVersion = new ARConsoleVersion();
      this.orderItem.t2ConsoleVersion.id = null;
    }
    if (restoreT2FertilizerType) {
      this.orderItem.t2FertilizerType = new ARFertilizerType();
      this.orderItem.t2FertilizerType.id = null;
    }

    if (restoreT3ConsoleModulationType) {
      this.orderItem.t3ConsoleModulationType = new ARConsoleModulationType();
      this.orderItem.t3ConsoleModulationType.id = null;
    }
    if (restoreT3ConsoleFormatType) {
      this.orderItem.t3ConsoleFormatType = new ARConsoleFormatType();
      this.orderItem.t3ConsoleFormatType.id = null;
    }
    if (restoreT3ConsoleType) {
      this.orderItem.t3ConsoleType = new ARConsoleType();
      this.orderItem.t3ConsoleType.id = null;
    }
    if (restoreT3ConsoleVersion) {
      this.orderItem.t3ConsoleVersion = new ARConsoleVersion();
      this.orderItem.t3ConsoleVersion.id = null;
    }
    if (restoreT3FertilizerType) {
      this.orderItem.t3FertilizerType = new ARFertilizerType();
      this.orderItem.t3FertilizerType.id = null;
    }

    if (restorePastAzoteFrequency) {
      this.orderItem.pastAzoteFrequency = new ARBleAzoteFrequency();
      this.orderItem.pastAzoteFrequency.id = null;
    }

    if (restoreVerseApportMoAutomne) {
      this.orderItem.verseApportMoAutomne = new ARVerseApportMoAutomne();
      this.orderItem.verseApportMoAutomne.id = null;
    }

    if (restoreVerseApportsMinerauxFractionnement) {
      this.orderItem.verseApportsMinerauxFractionnement = new ARVerseApportsMinerauxFractionnement();
      this.orderItem.verseApportsMinerauxFractionnement.id = null;
    }

    // TODO suppr champs agrofert

    if (restoreEtatVegetatifSh) {
      this.orderItem.etatVegetatifSh = new ARBleEtatVegetatifSh();
      this.orderItem.etatVegetatifSh.id = null;
    }
    if (restoreFirstAzoteProductType) {
      this.orderItem.firstAzoteProductType = new ARBleAzoteProductType();
      this.orderItem.firstAzoteProductType.id = null;
    }
    if (restoreFirstAzotePeriod) {
      this.orderItem.firstAzotePeriod = new ARBleAzotePeriod();
      this.orderItem.firstAzotePeriod.id = null;
    }
    if (restoreSecondAzoteProductType) {
      this.orderItem.secondAzoteProductType = new ARBleAzoteProductType();
      this.orderItem.secondAzoteProductType.id = null;
    }
    if (restoreSecondAzotePeriod) {
      this.orderItem.secondAzotePeriod = new ARBleAzotePeriod();
      this.orderItem.secondAzotePeriod.id = null;
    }
    if (restorePastAzoteProductType) {
      this.orderItem.pastAzoteProductType = new ARBleAzoteProductType();
      this.orderItem.pastAzoteProductType.id = null;
    }
    if (restorePastAzotePreviousCultureProductType) {
      this.orderItem.pastAzotePreviousCultureProductType = new ARBleAzoteProductType();
      this.orderItem.pastAzotePreviousCultureProductType.id = null;
    }
    if (restorePastResiduType) {
      this.orderItem.pastResiduType = new ARBlePastResiduType();
      this.orderItem.pastResiduType.id = null;
    }
    if (restorePrairieAge) {
      this.orderItem.prairieAge = new ARBlePrairieAge();
      this.orderItem.prairieAge.id = null;
    }
    if (restorePrairieDestructionPeriod) {
      this.orderItem.prairieDestructionPeriod = new ARBlePrairieDestructionPeriod();
      this.orderItem.prairieDestructionPeriod.id = null;
    }
    if (restorePrairiePostDestructionRank) {
      this.orderItem.prairiePostDestructionRank = new ARBlePrairiePostDestructionRank();
      this.orderItem.prairiePostDestructionRank.id = null;
    }
    if (restorePrairieExploitationMode) {
      this.orderItem.prairieExploitationMode = new ARBlePrairieExploitationMode();
      this.orderItem.prairieExploitationMode.id = null;
    }
    if (restorePrairieType) {
      this.orderItem.prairieType = new ARBlePrairieType();
      this.orderItem.prairieType.id = null;
    }
    if (restoreCIType) {
      this.orderItem.ciType = new ARBleCIType();
      this.orderItem.ciType.id = null;
    }
    if (restoreCIDevelopment) {
      this.orderItem.ciDevelopment = new ARBleCIDevelopment();
      this.orderItem.ciDevelopment.id = null;
    }
    if (restoreCIDestructionPeriod) {
      this.orderItem.ciDestructionPeriod = new ARBleCIDestructionPeriod();
      this.orderItem.ciDestructionPeriod.id = null;
    }
  }

  getParcel(parcelId: number) {
    this.orgeService.getParcel(parcelId).subscribe(
      (parcel) => {
        this.parcel = parcel;
      },
      (error) => this.handleGetError(error),
    );
  }

  getCampaigns() {
    this.orgeService.getCampaigns().subscribe((data) => {
      this.handleCampaigns(data);
    });
  }

  handleCampaigns(campaigns: AROrgeCampaign[]) {
    campaigns = campaigns.filter((x) => AROrgeUtils.isCampaignCurrentlyOpen(x));
    if (this.isBatch) {
      // on suppose qu'on est dans la dernière campagne
      // TODO  vérifier avec les parcelles; normalement pas éditable sinon
      if (campaigns.length > 0) {
        this.currentCampaign = campaigns[0];
      }
    } else {
      for (const campaign of campaigns) {
        if (campaign.id === this.parcel.campaign.id) {
          this.currentCampaign = campaign;
        }
      }
    }

    if (this.currentCampaign) {
      this.isT1OrderCurrentlyOpen = AROrgeUtils.isT1OrderCurrentlyOpen(this.currentCampaign);
      this.isT2OrderCurrentlyOpen = AROrgeUtils.isT2OrderCurrentlyOpen(this.currentCampaign);
      this.isT3OrderCurrentlyOpen = AROrgeUtils.isT3OrderCurrentlyOpen(this.currentCampaign);

      this.isT1DataCurrentlyOpen = AROrgeUtils.isT1DataCurrentlyOpen(this.currentCampaign);
      this.isT2DataCurrentlyOpen = AROrgeUtils.isT2DataCurrentlyOpen(this.currentCampaign);
      this.isT3DataCurrentlyOpen = AROrgeUtils.isT3DataCurrentlyOpen(this.currentCampaign);
    }
  }

  checkCampaignConfigurationDates() {
    if (!this.isBatch) {
      if (!!this.currentCampaign && !this.orderItem.order) {
        // Réinitialiser les T1, T2 et T3 si la commande n'est pas encore passée
        // et commande plus possible
        if (this.orderItem.hasT1 && !this.isT1OrderCurrentlyOpen) {
          this.orderItem.hasT1 = false;
          this.prepareNewT1Data();
          this.isT1OrderPassedWarning = true;
        }
        if (!this.orderItem.hasT1 && this.orderItem.hasT2 && !this.isT2OrderCurrentlyOpen) {
          this.orderItem.hasT2 = false;
          this.prepareNewT2Data();
          this.isT2OrderPassedWarning = true;
        }
        if (!this.orderItem.hasT1 && !this.orderItem.hasT2 && this.orderItem.hasT3 && !this.isT3OrderCurrentlyOpen) {
          this.orderItem.hasT3 = false;
          this.prepareNewT3Data();
          this.isT3OrderPassedWarning = true;
        }
      }
    }
  }

  private sorting(attr: string) {
    return (a: any, b: any) => (!a[attr] ? -1 : a[attr].localeCompare(b[attr]));
  }

  newOrderItem(parcelId: number) {
    this.orderItem = new AROrgeOrderItem();
    this.orderItem.parcel = new AROrgeParcel();

    this.orderItem.parcel.id = parcelId;

    this.prepareNewT1Data();
    this.prepareNewT2Data();
    this.prepareNewT3Data();

    this.prepareFertilizerData();
    this.prepareVraData();
    this.computeAdviceTypeLock();
  }

  prepareNewT1Data(orderItem: any = this.orderItem, value: any = null) {
    orderItem.t1AzoteToSpread = value;
    this.prepareNewT1ZonageData(orderItem);
    this.prepareNewT1FertilizerData(orderItem);
  }

  prepareNewT1ZonageData(orderItem: any = this.orderItem, value: any = null) {
    orderItem.t1ConsoleFormatType = value;
    orderItem.t1ConsoleType = value;
    orderItem.t1ConsoleVersion = value;
    orderItem.t1ConsoleModulationType = value;
    orderItem.t1OtherConsoleVersion = value;
  }

  prepareNewT1FertilizerData(orderItem: any = this.orderItem, value: any = null) {
    orderItem.t1FertilizerConcentration = value;
    orderItem.t1FertilizerType = value;
  }

  prepareNewT2Data(orderItem: any = this.orderItem, value: any = null) {
    orderItem.t2AzoteToSpread = value;
    this.prepareNewT2ZonageData(orderItem);
    this.prepareNewT2FertilizerData(orderItem);
  }

  prepareNewT2ZonageData(orderItem: any = this.orderItem, value: any = null) {
    orderItem.t2ConsoleFormatType = value;
    orderItem.t2ConsoleType = value;
    orderItem.t2ConsoleVersion = value;
    orderItem.t2ConsoleModulationType = value;
    orderItem.t2OtherConsoleVersion = value;
  }

  prepareNewT2FertilizerData(orderItem: any = this.orderItem, value: any = null) {
    orderItem.t2FertilizerConcentration = value;
    orderItem.t2FertilizerType = value;
  }

  prepareNewT3Data(orderItem: any = this.orderItem, value: any = null) {
    orderItem.t3AzoteToSpread = value;
    this.prepareNewT3ZonageData(orderItem);
    this.prepareNewT3FertilizerData(orderItem);
  }

  prepareNewT3ZonageData(orderItem: any = this.orderItem, value: any = null) {
    orderItem.t3ConsoleFormatType = value;
    orderItem.t3ConsoleType = value;
    orderItem.t3ConsoleVersion = value;
    orderItem.t3ConsoleModulationType = value;
    orderItem.t3OtherConsoleVersion = value;
  }

  prepareNewT3FertilizerData(orderItem: any = this.orderItem, value: any = null) {
    orderItem.t3FertilizerConcentration = value;
    orderItem.t3FertilizerType = value;
  }

  prepareOrderItem() {
    this.onChangeOrderT1(this.orderItem.hasT1, true);
    this.onChangeOrderT2(this.orderItem.hasT2, true);
    this.onChangeOrderT3(this.orderItem.hasT3, true);
    this.prepareConsoleModulationType();
    this.prepareFertilizerData();
    this.prepareVraData();
    this.computeAdviceTypeLock();
  }

  prepareConsoleModulationType() {
    // toutes les memes :
    // donc on prend la premiere
    // TODO remplacer les champs dans la base par un seul commun
    this.commonConsoleModulationType = new ARConsoleModulationType();
    if (this.orderItem.t1ConsoleModulationType) {
      this.commonConsoleModulationType.id = this.orderItem.t1ConsoleModulationType.id;
    } else if (this.orderItem.t2ConsoleModulationType) {
      this.commonConsoleModulationType.id = this.orderItem.t2ConsoleModulationType.id;
    } else if (this.orderItem.t3ConsoleModulationType) {
      this.commonConsoleModulationType.id = this.orderItem.t3ConsoleModulationType.id;
    }
  }

  prepareFertilizerData() {
    this.t1FertilizerData = new ARFertilizerT1DataFacade(this.orderItem);
    this.t2FertilizerData = new ARFertilizerT2DataFacade(this.orderItem);
    this.t3FertilizerData = new ARFertilizerT3DataFacade(this.orderItem);
  }

  prepareVraData() {
    this.t1VraData = new ARVraT1DataFacade(this.orderItem);
    this.t2VraData = new ARVraT2DataFacade(this.orderItem);
    this.t3VraData = new ARVraT3DataFacade(this.orderItem);
  }

  private computeAdviceTypeLock(): void {
    if (this.isAdmin) {
      this.isAdviceTypeLocked = false;
      return;
    }

    const items = this.isBatch ? this.batchOrderItems : [this.orderItem];
    if (!items || items.length === 0) {
      this.isAdviceTypeLocked = false;
      return;
    }

    const dates = this.collectRestitutionDatesFromOrderItems(items);
    if (dates.length === 0) {
      this.isAdviceTypeLocked = false;
      return;
    }

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    this.isAdviceTypeLocked = dates.some((date) => date.getTime() - today.getTime() < this.adviceTypeLockThresholdMs);
  }

  private collectRestitutionDatesFromOrderItems(items: AROrgeOrderItem[]): Date[] {
    const dates: Date[] = [];

    items
      .filter((item) => !!item && !!item.order)
      .forEach((item) => {
        const campaignYear = this.getCampaignYearFromOrderItem(item);
        if (item.hasT2) {
          this.addRestitutionDate(dates, item.t2_date_livraison, item.defaultWeekT2, campaignYear);
        }
        if (item.hasT3) {
          this.addRestitutionDate(dates, item.t3_date_livraison, item.defaultWeekT3, campaignYear);
        }
      });

    return dates;
  }

  private addRestitutionDate(target: Date[], primary: Date, fallback: any, campaignYear: number): void {
    const normalizedPrimary = this.normalizeRestitutionDate(primary, campaignYear);
    if (normalizedPrimary) {
      target.push(normalizedPrimary);
      return;
    }

    const normalizedFallback = this.normalizeRestitutionDate(fallback, campaignYear);
    if (normalizedFallback) {
      target.push(normalizedFallback);
    }
  }

  private normalizeRestitutionDate(date: any, campaignYear?: number): Date {
    if (!date) {
      return null;
    }

    if (date instanceof Date) {
      const parsed = new Date(date.getTime());
      parsed.setHours(0, 0, 0, 0);
      const corrected = correctDate(parsed);
      corrected.setHours(0, 0, 0, 0);
      return corrected;
    }

    if (typeof date === 'string' || typeof date === 'number') {
      const parsed = new Date(date as any);
      if (isNaN(parsed.getTime())) {
        return null;
      }
      const corrected = correctDate(parsed);
      corrected.setHours(0, 0, 0, 0);
      return corrected;
    }

    if (typeof date === 'object') {
      const defaultWeekDate = this.normalizeDefaultWeek(date, campaignYear);
      if (defaultWeekDate) {
        return defaultWeekDate;
      }

      if (date.date) {
        return this.normalizeRestitutionDate(date.date, campaignYear);
      }

      return null;
    }

    return null;
  }

  private normalizeDefaultWeek(value: any, campaignYear?: number): Date {
    if (!value || typeof value.week === 'undefined') {
      return null;
    }

    const week = this.parseWeekNumber(value.week);
    if (!week || week <= 0) {
      return null;
    }

    let year = this.extractYearValue(campaignYear);
    if (!year) {
      year = this.extractYearValue(value.year);
    }

    if (!year) {
      return null;
    }

    const weekDate = this.getDateForIsoWeek(year, week);
    if (!weekDate) {
      return null;
    }

    const corrected = correctDate(weekDate);
    corrected.setHours(0, 0, 0, 0);
    return corrected;
  }

  private getDateForIsoWeek(year: number, week: number): Date {
    if (!year || !week || week < 1) {
      return null;
    }

    const januaryFourth = new Date(year, 0, 4);
    const januaryFourthDay = januaryFourth.getDay() || 7;
    const weekStart = new Date(januaryFourth.getTime());
    weekStart.setDate(januaryFourth.getDate() - januaryFourthDay + 1 + (week - 1) * 7);
    weekStart.setHours(0, 0, 0, 0);
    return weekStart;
  }

  private extractYearValue(value: any): number {
    if (value === null || value === undefined) {
      return null;
    }

    if (typeof value === 'number') {
      return isNaN(value) ? null : value;
    }

    if (value instanceof Date) {
      const copy = new Date(value.getTime());
      return isNaN(copy.getTime()) ? null : copy.getUTCFullYear();
    }

    if (typeof value === 'string') {
      const trimmed = value.trim();
      if (trimmed === '') {
        return null;
      }

      const parsedDate = new Date(trimmed);
      if (!isNaN(parsedDate.getTime())) {
        return parsedDate.getUTCFullYear();
      }

      const numeric = parseInt(trimmed, 10);
      return isNaN(numeric) ? null : numeric;
    }

    if (typeof value === 'object') {
      if (typeof value.year !== 'undefined') {
        return this.extractYearValue(value.year);
      }
      if (typeof value.campaignYear !== 'undefined') {
        return this.extractYearValue(value.campaignYear);
      }
    }

    return null;
  }

  private parseWeekNumber(value: any): number {
    if (typeof value === 'number') {
      return isNaN(value) ? null : Math.floor(value);
    }
    if (typeof value === 'string') {
      const parsed = parseInt(value, 10);
      return isNaN(parsed) ? null : parsed;
    }
    return null;
  }

  private getCampaignYearFromOrderItem(orderItem: AROrgeOrderItem): number {
    if (!orderItem || !orderItem.order || !orderItem.order.campaign) {
      return null;
    }

    const campaign = orderItem.order.campaign;

    let year = this.extractYearValue(campaign.campaignYear);
    if (!year) {
      year = this.extractYearValue(campaign.year);
    }

    if (!year) {
      year = this.extractYearValue(campaign.endingOn);
    }

    if (!year) {
      year = this.extractYearValue(campaign.startingOn);
    }

    if (!year && campaign.configuration) {
      const config = campaign.configuration;
      const candidates = [
        config.startingOn,
        config.orderT1EndingOn,
        config.dataT1EndingOn,
        config.orderT2EndingOn,
        config.dataT2EndingOn,
        config.dataT3StartingOn,
        config.orderT3EndingOn,
        config.dataT3EndingOn,
      ];

      for (const candidate of candidates) {
        year = this.extractYearValue(candidate);
        if (year) {
          break;
        }
      }
    }

    return year;
  }

  addOrder() {
    this.withinOrder = true;
    this.save();
  }

  confirmOrder(parcelId: number) {
    this.router.navigate(['/orders', 'azote', 'orge', 'orders', 'new', parcelId]);
  }

  onChangeOrder3Passages() {
    if (this.isAdviceTypeLocked) {
      return;
    }

    if (this.orderItem.hasZonage === false) {
      this.onChangeZonage(2);
    }

    this.onChangeOrderT1(true);
    this.onChangeOrderT2(true);
    this.onChangeOrderT3(true);
    this.orderItem.hasT1 = true;
    this.orderItem.hasT2 = true;
    this.orderItem.hasT3 = true;
    this.orderItem.hasZonage = true;
  }

  onChangeOrder2Passages() {
    if (this.isAdviceTypeLocked) {
      return;
    }

    if (this.orderItem.hasZonage === false) {
      this.onChangeZonage(2);
    }

    this.onChangeOrderT1(false);
    this.onChangeOrderT2(true);
    this.onChangeOrderT3(true);
    this.orderItem.hasT1 = false;
    this.orderItem.hasT2 = true;
    this.orderItem.hasT3 = true;
    this.orderItem.hasZonage = true;
  }

  onChangeOrder1Passage() {
    if (this.isAdviceTypeLocked) {
      return;
    }

    this.onChangeOrderT1(false);
    this.onChangeOrderT2(false);
    this.onChangeOrderT3(true);
    this.orderItem.hasT1 = false;
    this.orderItem.hasT2 = false;
    this.orderItem.hasT3 = true;
    this.orderItem.hasZonage = true;
  }

  onChangeOrderT1(isOrderedT1: any, isInit = false) {
    isOrderedT1 = toBoolean(isOrderedT1);
    if (!isInit && !isOrderedT1) {
      this.prepareNewT1Data();
    }
    if (!isInit) {
      this.hasT1Changed = true;

      if (this.commonConsoleModulationType) {
        this.orderItem.t1ConsoleModulationType = new ARConsoleModulationType();
        this.orderItem.t1ConsoleModulationType.id = this.commonConsoleModulationType.id;
      }
    }
    if (this.isBatch) {
      (<any>this.cbBatchEdit)['hasT1'] = isOrderedT1 === true;
    }
  }

  onChangeOrderT2(isOrderedT2: any, isInit = false) {
    isOrderedT2 = toBoolean(isOrderedT2);
    if (!isInit && !isOrderedT2) {
      this.prepareNewT2Data();
    }
    if (!isInit) {
      this.hasT2Changed = true;

      if (this.commonConsoleModulationType) {
        this.orderItem.t2ConsoleModulationType = new ARConsoleModulationType();
        this.orderItem.t2ConsoleModulationType.id = this.commonConsoleModulationType.id;
      }
    }
    if (this.isBatch) {
      (<any>this.cbBatchEdit)['hasT2'] = isOrderedT2 === true;
    }
  }

  onChangeOrderT3(isOrderedT3: any, isInit = false) {
    isOrderedT3 = toBoolean(isOrderedT3);

    if (!isInit && !isOrderedT3) {
      // vider les valeurs
      this.prepareNewT3Data();
      if (this.isBatch) {
        // "cocher" les checkbox pour prendre en compte le vidage dans le batch
        this.prepareNewT3Data(this.cbBatchEdit, true);
      }
    }
    if (!isInit) {
      this.hasT3Changed = true;

      if (this.commonConsoleModulationType) {
        this.orderItem.t3ConsoleModulationType = new ARConsoleModulationType();
        this.orderItem.t3ConsoleModulationType.id = this.commonConsoleModulationType.id;
      }
    }
    if (this.isBatch) {
      (<any>this.cbBatchEdit)['hasT3'] = isOrderedT3 === true;
    }
  }

  onChangeZonage(zonageChoice: number) {
    if (this.isAdviceTypeLocked) {
      return;
    }

    (<any>this.cbBatchEdit)['consoleModulationType'] = true;

    if (zonageChoice == 1) {
      this.orderItem.hasZonage = false;
      this.commonConsoleModulationType = null;

      // rien de spécial
      // effacer les infos de zonage
      this.prepareNewT1ZonageData();
      this.prepareNewT2ZonageData();
      this.prepareNewT3ZonageData();
    } else if (zonageChoice == 2) {
      this.orderItem.hasZonage = true;

      this.prepareNewT1ZonageData();
      this.prepareNewT2ZonageData();
      this.prepareNewT3ZonageData();

      // id directement ; suppose pareil sur tous les env
      // pas de zonage console
      this.commonConsoleModulationType = new ARConsoleModulationType();
      this.commonConsoleModulationType.id = 1;

      if (this.orderItem.hasT1) {
        this.orderItem.t1ConsoleModulationType = new ARConsoleModulationType();
        this.orderItem.t1ConsoleModulationType.id = this.commonConsoleModulationType.id;
      }

      if (this.orderItem.hasT2) {
        this.orderItem.t2ConsoleModulationType = new ARConsoleModulationType();
        this.orderItem.t2ConsoleModulationType.id = this.commonConsoleModulationType.id;
      }

      if (this.orderItem.hasT3) {
        this.orderItem.t3ConsoleModulationType = new ARConsoleModulationType();
        this.orderItem.t3ConsoleModulationType.id = this.commonConsoleModulationType.id;
      }
    } else if (zonageChoice == 3) {
      this.orderItem.hasZonage = true;

      this.commonConsoleModulationType = new ARConsoleModulationType();
      this.commonConsoleModulationType.id = 2;

      if (this.orderItem.hasT1) {
        this.orderItem.t1ConsoleModulationType = new ARConsoleModulationType();
        this.orderItem.t1ConsoleModulationType.id = this.commonConsoleModulationType.id;
        if (this.vraEditT1Ref.first) {
          this.vraEditT1Ref.first.onChangeConsoleModulationType(this.orderItem.t1ConsoleModulationType.id);
        }
      } else {
        this.orderItem.t1ConsoleModulationType = null;
      }

      if (this.orderItem.hasT2) {
        this.orderItem.t2ConsoleModulationType = new ARConsoleModulationType();
        this.orderItem.t2ConsoleModulationType.id = this.commonConsoleModulationType.id;
        if (this.vraEditT2Ref.first) {
          this.vraEditT2Ref.first.onChangeConsoleModulationType(this.orderItem.t2ConsoleModulationType.id);
        }
      } else {
        this.orderItem.t2ConsoleModulationType = null;
      }

      if (this.orderItem.hasT3) {
        this.orderItem.t3ConsoleModulationType = new ARConsoleModulationType();
        this.orderItem.t3ConsoleModulationType.id = this.commonConsoleModulationType.id;
        if (this.vraEditT3Ref.first) {
          this.vraEditT3Ref.first.onChangeConsoleModulationType(this.orderItem.t3ConsoleModulationType.id);
        }
      } else {
        this.orderItem.t3ConsoleModulationType = null;
      }
    } else if (zonageChoice == 4) {
      this.orderItem.hasZonage = true;

      this.commonConsoleModulationType = new ARConsoleModulationType();
      this.commonConsoleModulationType.id = 3;

      if (this.orderItem.hasT1) {
        this.orderItem.t1ConsoleModulationType = new ARConsoleModulationType();
        this.orderItem.t1ConsoleModulationType.id = this.commonConsoleModulationType.id;
        if (this.vraEditT1Ref.first) {
          this.vraEditT1Ref.first.onChangeConsoleModulationType(this.orderItem.t1ConsoleModulationType.id);
        }
      } else {
        this.orderItem.t1ConsoleModulationType = null;
      }

      if (this.orderItem.hasT2) {
        this.orderItem.t2ConsoleModulationType = new ARConsoleModulationType();
        this.orderItem.t2ConsoleModulationType.id = this.commonConsoleModulationType.id;
        if (this.vraEditT2Ref.first) {
          this.vraEditT2Ref.first.onChangeConsoleModulationType(this.orderItem.t2ConsoleModulationType.id);
        }
      } else {
        this.orderItem.t2ConsoleModulationType = null;
      }

      if (this.orderItem.hasT3) {
        this.orderItem.t3ConsoleModulationType = new ARConsoleModulationType();
        this.orderItem.t3ConsoleModulationType.id = this.commonConsoleModulationType.id;
        if (this.vraEditT3Ref.first) {
          this.vraEditT3Ref.first.onChangeConsoleModulationType(this.orderItem.t3ConsoleModulationType.id);
        }
      } else {
        this.orderItem.t3ConsoleModulationType = null;
      }
    }
  }

  onChangePilotage(hasPilotage: boolean) {
    if (this.isAdviceTypeLocked) {
      return;
    }

    hasPilotage = toBoolean(hasPilotage);
    this.orderItem.isT3Pilotage = hasPilotage;
    if (hasPilotage) {
      // dose pas besoin (sera calculée par Wanaka)
      this.orderItem.t3AzoteToSpread = null;
    } else {
      // si pas pilotage : Non (pas de zonage) non autorisé
      if (this.orderItem.hasZonage === false) {
        this.onChangeZonage(2);
      }
    }
  }

  onChangeT1Fertilizer() {
    if (
      !this.orderItem.t1FertilizerConcentration &&
      !(this.orderItem.t1FertilizerType && this.orderItem.t1FertilizerType.id)
    ) {
      return;
    }

    if (!this.orderItem.t2FertilizerConcentration || this.t1t2Links.get('t2FertilizerConcentration')) {
      if (!this.isBatch || (<any>this.cbBatchEdit)['t2FertilizerConcentration']) {
        this.orderItem.t2FertilizerConcentration = this.orderItem.t1FertilizerConcentration;
        this.t1t2Links.set('t2FertilizerConcentration', true);
      }
    }
    if (
      this.orderItem.t1FertilizerType &&
      this.orderItem.t1FertilizerType.id &&
      (!this.orderItem.t2FertilizerType ||
        !this.orderItem.t2FertilizerType.id ||
        this.t1t2Links.get('t2FertilizerType'))
    ) {
      if (!this.isBatch || (<any>this.cbBatchEdit)['t2FertilizerType']) {
        this.orderItem.t2FertilizerType = new ARFertilizerType();
        this.orderItem.t2FertilizerType.id = this.orderItem.t1FertilizerType.id;
        this.t1t2Links.set('t2FertilizerType', true);
      }
    }

    this.onChangeT2Fertilizer(false);
  }

  onChangeT2Fertilizer(isEvent: boolean = true) {
    if (isEvent) {
      this.t1t2Links.set('t2FertilizerConcentration', false);
      this.t1t2Links.set('t2FertilizerType', false);
    }

    // seulement si pas déjà rempli
    if (!this.orderItem.t3FertilizerConcentration || this.t2t3Links.get('t3FertilizerConcentration')) {
      if (!this.isBatch || (<any>this.cbBatchEdit)['t3FertilizerConcentration']) {
        this.orderItem.t3FertilizerConcentration = this.orderItem.t2FertilizerConcentration;
        this.t2t3Links.set('t3FertilizerConcentration', true);
      }
    }
    if (
      this.orderItem.t2FertilizerType &&
      this.orderItem.t2FertilizerType.id &&
      (!this.orderItem.t3FertilizerType ||
        !this.orderItem.t3FertilizerType.id ||
        this.t2t3Links.get('t3FertilizerType'))
    ) {
      if (!this.isBatch || (<any>this.cbBatchEdit)['t3FertilizerType']) {
        this.orderItem.t3FertilizerType = new ARFertilizerType();
        this.orderItem.t3FertilizerType.id = this.orderItem.t2FertilizerType.id;
        this.t2t3Links.set('t3FertilizerType', true);
      }
    }
  }

  onChangeT3Fertilizer(isEvent: boolean = true) {
    if (isEvent) {
      // changé manuellement => changer t2 ne va plus changer T3
      this.t2t3Links.set('t3FertilizerConcentration', false);
      this.t2t3Links.set('t3FertilizerType', false);
    }
  }

  onChangeT1Vra() {
    if (
      !(this.orderItem.t1ConsoleModulationType && this.orderItem.t1ConsoleModulationType.id) &&
      !(this.orderItem.t1ConsoleType && this.orderItem.t1ConsoleType.id) &&
      !(this.orderItem.t1ConsoleVersion && this.orderItem.t1ConsoleVersion.id) &&
      !(this.orderItem.t1ConsoleFormatType && this.orderItem.t1ConsoleFormatType.id) &&
      !this.orderItem.t1OtherConsoleVersion
    ) {
      return;
    }

    if (
      this.orderItem.t1ConsoleModulationType &&
      this.orderItem.t1ConsoleModulationType.id &&
      (!this.orderItem.t2ConsoleModulationType ||
        !this.orderItem.t2ConsoleModulationType.id ||
        this.t1t2Links.get('t2ConsoleModulationType'))
    ) {
      if (!this.isBatch || (<any>this.cbBatchEdit)['t2ConsoleModulationType']) {
        this.orderItem.t2ConsoleModulationType = new ARConsoleModulationType();
        this.orderItem.t2ConsoleModulationType.id = this.orderItem.t1ConsoleModulationType.id;
        this.t1t2Links.set('t2ConsoleModulationType', true);
        if (this.vraEditT2Ref.first) {
          this.vraEditT2Ref.first.onChangeConsoleModulationType(this.orderItem.t2ConsoleModulationType.id);
          this.t1t2Links.set('t2ConsoleType', true);
          this.t1t2Links.set('t2ConsoleVersion', true);
          this.t1t2Links.set('t2ConsoleFormatType', true);
          this.t1t2Links.set('t2OtherConsoleVersion', true);
        }
      }
    }
    if (
      this.orderItem.t1ConsoleType &&
      this.orderItem.t1ConsoleType.id &&
      (!this.orderItem.t2ConsoleType ||
        !this.orderItem.t2ConsoleType.id ||
        this.t1t2Links.get('t2ConsoleType'))
    ) {
      if (!this.isBatch || (<any>this.cbBatchEdit)['t2ConsoleType']) {
        this.orderItem.t2ConsoleType = new ARConsoleType();
        this.orderItem.t2ConsoleType.id = this.orderItem.t1ConsoleType.id;
        this.t1t2Links.set('t2ConsoleType', true);
      }
    }
    if (
      this.orderItem.t1ConsoleVersion &&
      this.orderItem.t1ConsoleVersion.id &&
      (!this.orderItem.t2ConsoleVersion ||
        !this.orderItem.t2ConsoleVersion.id ||
        this.t1t2Links.get('t2ConsoleVersion'))
    ) {
      if (!this.isBatch || (<any>this.cbBatchEdit)['t2ConsoleVersion']) {
        this.orderItem.t2ConsoleVersion = new ARConsoleVersion();
        this.orderItem.t2ConsoleVersion.id = this.orderItem.t1ConsoleVersion.id;
        this.t1t2Links.set('t2ConsoleVersion', true);
      }
    }
    if (
      this.orderItem.t1ConsoleFormatType &&
      this.orderItem.t1ConsoleFormatType.id &&
      (!this.orderItem.t2ConsoleFormatType ||
        !this.orderItem.t2ConsoleFormatType.id ||
        this.t1t2Links.get('t2ConsoleFormatType'))
    ) {
      if (!this.isBatch || (<any>this.cbBatchEdit)['t2ConsoleFormatType']) {
        this.orderItem.t2ConsoleFormatType = new ARConsoleFormatType();
        this.orderItem.t2ConsoleFormatType.id = this.orderItem.t1ConsoleFormatType.id;
        this.t1t2Links.set('t2ConsoleFormatType', true);
      }
    }
    if (!this.orderItem.t2OtherConsoleVersion || this.t1t2Links.get('t2OtherConsoleVersion')) {
      if (!this.isBatch || (<any>this.cbBatchEdit)['t2OtherConsoleVersion']) {
        this.orderItem.t2OtherConsoleVersion = this.orderItem.t1OtherConsoleVersion;
        this.t1t2Links.set('t2OtherConsoleVersion', true);
      }
    }

    this.onChangeT2Vra(false);
  }

  onChangeT2Vra(isEvent: boolean = true) {
    if (isEvent) {
      this.t1t2Links.set('t2ConsoleFormatType', false);
      this.t1t2Links.set('t2ConsoleType', false);
      this.t1t2Links.set('t2ConsoleVersion', false);
      this.t1t2Links.set('t2ConsoleModulationType', false);
      this.t1t2Links.set('t2OtherConsoleVersion', false);
    }

    if (
      (this.orderItem.t2ConsoleModulationType &&
        this.orderItem.t2ConsoleModulationType.id &&
        (!this.orderItem.t3ConsoleModulationType || !this.orderItem.t3ConsoleModulationType.id)) ||
      this.t2t3Links.get('t3ConsoleModulationType')
    ) {
      if (!this.isBatch || (<any>this.cbBatchEdit)['t3ConsoleModulationType']) {
        this.orderItem.t3ConsoleModulationType = new ARConsoleModulationType();
        this.orderItem.t3ConsoleModulationType.id = this.orderItem.t2ConsoleModulationType.id;
        this.t2t3Links.set('t3ConsoleModulationType', true);
        if (this.vraEditT3Ref.first) {
          this.vraEditT3Ref.first.onChangeConsoleModulationType(this.orderItem.t3ConsoleModulationType.id);
          // va changer les autres champs aussi => on les met dans links
          this.t2t3Links.set('t3ConsoleType', true);
          this.t2t3Links.set('t3ConsoleVersion', true);
          this.t2t3Links.set('t3ConsoleFormatType', true);
          this.t2t3Links.set('t3OtherConsoleVersion', true);
        }
      }
    }
    if (
      (this.orderItem.t2ConsoleType &&
        this.orderItem.t2ConsoleType.id &&
        (!this.orderItem.t3ConsoleType || !this.orderItem.t3ConsoleType.id)) ||
      this.t2t3Links.get('t3ConsoleType')
    ) {
      if (!this.isBatch || (<any>this.cbBatchEdit)['t3ConsoleType']) {
        this.orderItem.t3ConsoleType = new ARConsoleType();
        this.orderItem.t3ConsoleType.id = this.orderItem.t2ConsoleType.id;
        this.t2t3Links.set('t3ConsoleType', true);
      }
    }
    if (
      (this.orderItem.t2ConsoleVersion &&
        this.orderItem.t2ConsoleVersion.id &&
        (!this.orderItem.t3ConsoleVersion || !this.orderItem.t3ConsoleVersion.id)) ||
      this.t2t3Links.get('t3ConsoleVersion')
    ) {
      if (!this.isBatch || (<any>this.cbBatchEdit)['t3ConsoleVersion']) {
        this.orderItem.t3ConsoleVersion = new ARConsoleVersion();
        this.orderItem.t3ConsoleVersion.id = this.orderItem.t2ConsoleVersion.id;
        this.t2t3Links.set('t3ConsoleVersion', true);
      }
    }
    if (
      (this.orderItem.t2ConsoleFormatType &&
        this.orderItem.t2ConsoleFormatType.id &&
        (!this.orderItem.t3ConsoleFormatType || !this.orderItem.t3ConsoleFormatType.id)) ||
      this.t2t3Links.get('t3ConsoleFormatType')
    ) {
      if (!this.isBatch || (<any>this.cbBatchEdit)['t3ConsoleFormatType']) {
        this.orderItem.t3ConsoleFormatType = new ARConsoleFormatType();
        this.orderItem.t3ConsoleFormatType.id = this.orderItem.t2ConsoleFormatType.id;
        this.t2t3Links.set('t3ConsoleFormatType', true);
      }
    }

    if (!this.orderItem.t3OtherConsoleVersion || this.t2t3Links.get('t3OtherConsoleVersion')) {
      if (!this.isBatch || (<any>this.cbBatchEdit)['t3OtherConsoleVersion']) {
        this.orderItem.t3OtherConsoleVersion = this.orderItem.t2OtherConsoleVersion;
        this.t2t3Links.set('t3OtherConsoleVersion', true);
      }
    }
  }

  onChangeT3Vra(isEvent: boolean = true) {
    if (isEvent) {
      this.t2t3Links.set('t3ConsoleFormatType', false);
      this.t2t3Links.set('t3ConsoleType', false);
      this.t2t3Links.set('t3ConsoleVersion', false);
      this.t2t3Links.set('t3ConsoleModulationType', false);
      this.t2t3Links.set('t3OtherConsoleVersion', false);
    }
  }

  handleShowBeApi(organisation: Organisation) {
    // TODO quand i18n mergée : récupérer l'org depuis la structure
    const isAdmin = KeycloakService.auth.authz.hasRealmRole('ROLE_ADMIN');
    const isNegoce = KeycloakService.auth.authz.hasRealmRole('ROLE_NEGOCE');
    if (isAdmin || isNegoce) {
      this.showBeApi = true;
      return;
    }

    this.showBeApi = organisation.hasBeApi;
  }

  handleSaveError(error: any) {
    if (error.status === 401) {
      showErrorMessage(this.modal, this.i18nTranslator.translate('ar.edit.error.unauthorized.edit'));
    } else {
      // FIXME détailler les erreurs du formulaire
      // FIXME définir un format pour les erreurs de validation
      console.error(error);
      showErrorMessage(this.modal, this.i18nTranslator.translate('edit.error.generic'));
    }
  }

  handleGetError(error: any) {
    if (error.status === 401) {
      showErrorMessage(this.modal, this.i18nTranslator.translate('ar.edit.error.unauthorized.view')).then((r) => {
        this.router.navigate(['/orders', 'azote', 'orge', 'parcels']);
      });
    } else {
      console.error(error);
      showErrorMessage(this.modal, this.i18nTranslator.translate('error.generic')).then((r) => {
        this.router.navigate(['/orders', 'azote', 'orge', 'parcels']);
      });
    }
  }

  handleSavebatchError(error: any) {
    if (error.status === 401) {
      showErrorMessage(this.modal, this.i18nTranslator.translate('ar.edit.error.unauthorized.edit.multiParcels'));
    } else {
      // FIXME détailler les erreurs du formulaire
      // FIXME définir un format pour les erreurs de validation
      console.error(error);
      showErrorMessage(this.modal, this.i18nTranslator.translate('ar.edit.error.save.multiParcels'));
    }
  }

  ngOnDestroy() {
    this.sub.unsubscribe();
  }
}
